import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { Box, Button, Stack, Typography, Divider, InputAdornment } from '@mui/material';

import { PaperCard } from 'components/Card';
import { FormProvider, TextField, DateTimeInput, NumericFormat, MultiSelect, Select, CardAutocomplete, Checkbox } from 'components/Form';
import { useForm, useWatch } from 'react-hook-form';
import { AccordionListItem } from 'components/Button';
import { useParams } from 'react-router-dom';
import useYupValidationResolver from 'hook/useYupValidationResolver';
import { additionalDetailsSchema } from '../validation';
import { useUpdate } from '../api/update';
import { useCompetenceAreas } from 'api/getCompetenceAreas';
import dayjs from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { AdditionalPeriods } from 'components/AdditionalPeriod/AdditionalPeriod';
import { NutsCodes } from 'components/NutsCodes/NutsCodes';
import { PlacesField } from 'components/PlaceAutocomplete/PlacesField';
import { toPlaceInputs } from 'utils/jobAdLocations';
import { searchCompanyUsers } from 'features/companies/api/getCompanyUsers';
import { useAuth } from 'lib/auth';

const UserItem = ({ full_name, ...rest }) => (
  <Stack gap={1} alignItems={'flex-start !important'} {...rest}>
    <Typography variant="body1" fontWeight="bold">
      {full_name}
    </Typography>
  </Stack>
);

const getDefaultValues = () => ({
  headline: '',
  role: '',
  experienceLevel: '',
  description: '',
  type: 'On site',
  hoursPerWeek: 40,
  maxRate: '',
  referenceNumber: '',
  startDate: null,
  endDate: null,
  applicationType: 'email',
  applicationEmail: '',
  applicationURL: '',
  lastDayToRespond: null,
  questionsEndDate: null,
  // agreementName: '',
  buyerName: '',
  locations: [],
  places: [],
  usesNuts: false,
  additionalPeriods: [],
  competenceAreas: [],
  hideBuyer: false,
});

export const EditForm = ({ jobAd, refetch }) => {
  let { id } = useParams();
  const { account } = useAuth();

  const [open, setOpen] = useState(false);
  const [selectedCompetenceAreas, setSelectedCompetenceAreas] = useState(jobAd?.competence_areas);
  const [initialLocations, setInitialLocations] = useState(jobAd?.locations);
  const [locations, setLocations] = useState(jobAd?.locations);
  // const [ selectedLocations, setSelectedLocations ] = useState([]);
  const [additionalPeriods, setAdditionalPeriods] = useState(jobAd?.additional_periods);
  const [lastDayToRespond, setLastDayToRespond] = useState(jobAd?.last_day_to_respond);
  const [questionsEndDate, setQuestionsEndDate] = useState(jobAd?.questions_end_date);
  const [initialPeriod, setInitialPeriod] = useState([
    {
      label: 'Initial',
      startDate: jobAd?.start_date,
      endDate: jobAd?.end_date,
      status: 'used',
    },
  ]);
  const [competencesArray, setCompetencesArray] = useState([]);
  const { data: competences } = useCompetenceAreas();

  const methods = useForm({
    defaultValues: getDefaultValues(),
    resolver: useYupValidationResolver(additionalDetailsSchema),
  });

  const { mutateAsync } = useUpdate(refetch);

  const handleClose = () => {
    setOpen(false);
    methods.reset({ ...getDefaultValues() });
  };

  const onSubmit = (values) => {
    mutateAsync({
      values,
      jobAdID: id,
    })
      .then(handleClose)
      .catch(() => {});
  };

  const handleAdditionalPeriods = (value) => {
    setAdditionalPeriods(value);
    methods.setValue("additionalPeriods", value);
  }

  const handleCompetenceAreasChange = (value) => {
    setSelectedCompetenceAreas(value);
    methods.setValue("competenceAreas", value);
    if (value.length > 0) {
      methods.trigger("competenceAreas");
    }
  };

  const applicationMethodLocked = jobAd?.application_method === 'in_app' && jobAd?.applications_count > 0;
  const inAppOnly = ['Framework', 'DPS', 'Direct'].includes(jobAd?.owner_type);

  const applicationTypeOptions = [
    ...(inAppOnly ? [] : [
      {label: "Email", value: "email", disabled: applicationMethodLocked},
      {label: "Website URL", value: "url", disabled: applicationMethodLocked},
    ]),
    {label: "In app", value: "in_app"},
  ];

  const { control } = methods;
  
  const applicationTypeValue = useWatch({
    control,
    name: 'applicationType',
  });

  useEffect(() => {
    if (!open) {
      methods.reset({ ...getDefaultValues() });
      document.body.style.overflow = '';
    } else if (open) {
      document.body.style.overflow = 'hidden';
      const {
        headline,
        buyer_id,
        buyer_name,
        // fa_id,
        // fa_name,
        // dps_id,
        // dps_name,
        role,
        experience_level,
        description,
        type,
        hours_per_week,
        max_rate,
        reference_number,
        competence_areas,
        start_date,
        end_date,
        additional_periods,
        application_method,
        application_email,
        application_url,
        last_day_to_respond,
        questions_end_date,
        supplier_id,
        hide_buyer,
      } = jobAd;
    
      methods.reset({
        // agreementOrDps: fa_id,
        buyer: buyer_id,
        // dps: dps_id,
        headline: headline,
        role: role,
        experienceLevel: experience_level,
        description: description,
        type: type,
        hoursPerWeek: hours_per_week,
        maxRate: max_rate,
        referenceNumber: reference_number,
        locations: selectedLocations,
        places: toPlaceInputs(jobAd?.places),
        usesNuts: !!jobAd?.uses_nuts_locations,
        competenceAreas: competence_areas,
        startDate: dayjs(new Date(start_date)),
        endDate: dayjs(new Date(end_date)),
        additionalPeriods: additional_periods,
        applicationType: inAppOnly ? 'in_app' : application_method,
        applicationEmail: application_email,
        applicationURL: application_url,
        lastDayToRespond: dayjs(new Date(last_day_to_respond)),
        questionsEndDate: questions_end_date ? dayjs(new Date(questions_end_date)) : null,
        supplierID: supplier_id,
        // agreementName: fa_name,
        buyerName: buyer_name,
        // dpsName: dps_name,
        hideBuyer: hide_buyer,
      });
    
      setAdditionalPeriods(jobAd?.additional_periods);
      const tempPeriod = [...initialPeriod];
      tempPeriod[0].startDate = jobAd?.start_date;
      tempPeriod[0].endDate = jobAd?.end_date;
      setInitialPeriod(tempPeriod);
      setSelectedCompetenceAreas(jobAd?.competence_areas);
      setInitialLocations(jobAd?.locations);
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [open, jobAd, methods, inAppOnly]);

  useEffect(() => {
    if (competences && Array.isArray(competences.competences)) {
      const allCompetences = competences.competences.map(item => ({
        value: item.id,
        label: item.name,
        ...item,
      }));

      setCompetencesArray(allCompetences);
    }
  }, [competences]);

  const handleDateChange = (date, field) => {
    if (field === 'lastDayToRespond') {
      setLastDayToRespond(date);
    }
    if (field === 'questionsEndDate') {
      setQuestionsEndDate(date);
    }

    if (methods !== null) {
      methods.setValue(field, date);
    }

    if(date !== null) {
      methods.trigger(field);
    }
    if (field === 'lastDayToRespond' && methods.getValues('questionsEndDate')) {
      methods.trigger('questionsEndDate');
    }
  };

  const selectedLocations = useMemo(() => {
    if (!initialLocations || initialLocations.length === 0) {
      return [];
    }
    
    const mapData = (locationNodes) => locationNodes.map(node => node.Code);
    
    return mapData(initialLocations);
  }, [initialLocations]);

  // useEffect(() => {
  //   setLocations(selectedLocations);
  //   methods.setValue("locations", selectedLocations);
  //   console.log(methods.getValues());
  // }, [selectedLocations]);
  
  const EditFormContent = (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        height: '100%',
        width: '100%',
        background: '#F3F4F6',
        justifyContent: 'center',
        display: 'flex',
        zIndex: (theme) => theme.zIndex.modal,
        alignItems: 'flex-start',
        overflow: 'hidden',
      }}
    >
      <Stack
        sx={{
          position: 'relative',
          width: '100%',
          height: `calc(100% - 69px)`,
          marginTop: 8.625,
          overflowY: 'auto',
          backgroundColor: '#fff',
        }}
      >
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
          <PaperCard
            height={69}
            sx={{
              background: '#fff',
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              px: 4,
              py: 2,
              border: 0,
              borderRadius: 0,
              boxShadow: 26,
              display: 'flex',
              boxSizing: 'border-box',
              zIndex: (theme) => theme.zIndex.modal + 1,
            }}
          >
            <Stack
              direction="row"
              sx={{
                alignItems: 'center',
                justifyContent: 'space-between',
                width: '100%',
              }}
            >
              <Typography>Editing ‘{jobAd?.headline}’</Typography>
              <Box
                sx={{
                  width: 165,
                  display: 'flex',
                  justifyContent: 'space-between',
                }}
              >
                <Button
                  size="medium"
                  onClick={handleClose}
                  variant="contained"
                  sx={({ palette }) => ({
                    width: 82,
                    boxShadow: 25,
                    color: palette.primary.main,
                    backgroundColor: '#fff',
                  })}
                >
                  Cancel
                </Button>
                <Button
                  size="medium"
                  type="submit"
                  variant="contained"
                  color="primary"
                  sx={{ width: 67 }}
                >
                  Save
                </Button>
              </Box>
            </Stack>
          </PaperCard>

          <Stack
            direction="row"
            width="100%"
            minHeight={`calc(100vh - 69px)`}
            height="100%"
            bgcolor={'#fff'}
            divider={<Divider orientation="vertical" flexItem />}
          >
            <Stack width={'50%'} direction="column" px={8} py={4}>
              {account.super_admin && jobAd?.owner_type === 'Intermediator' && (
                <Checkbox
                  name="hideBuyer"
                  label="Hide buyer name and logo from other companies"
                />
              )}
              <TextField
                name="headline"
                label="Headline"
                placeholder="Please enter Headline"
                required
                type="text"
                sx={{ mb: 2 }}
                fullWidth
              />
              <Stack direction={"row"} gap={2}>
                <TextField
                  name="role"
                  label="Role"
                  placeholder="Please enter Role"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
                <TextField
                  name="experienceLevel"
                  label="Experience level"
                  placeholder="Please enter Experience level"
                  required
                  type="text"
                  sx={{ mb: 2 }}
                  fullWidth
                />
              </Stack>
              <TextField
                name="description"
                label="Description"
                placeholder="Please enter description"
                type="text"
                fullWidth
                sx={{
                  mb: 2,
                  '& .MuiInputBase-root, .MuiInputBase-input': {
                    p: 0,
                  },
                }}
                multiline
                minRows={2}
              />
              <Stack direction={"row"} gap={2}>
                <Select
                  name="type"
                  label="Type"
                  options={[
                    { label: "On site", value: "On site" },
                    { label: "Hybrid", value: "Hybrid" },
                    { label: "Remote", value: "Remote" },
                  ]}
                  placeholder="Please select type"
                  wrapperSx={{ mb: 2 }}
                  required
                  fullWidth
                />
                <NumericFormat
                  name="hoursPerWeek"
                  label="Hours per week"
                  placeholder="Please enter hours per week"
                  type="input"
                  sx={{ mb: 2}}
                  wrapperSx={{width: 1}}
                  labelSx={{mb: -0.5}}
                  required
                />
              </Stack>
            </Stack>
            <Stack width={'50%'} direction="column" px={8} py={4}>
              <Stack direction={"row"} gap={2}>
                <NumericFormat
                  name="maxRate"
                  label="Max Rate"
                  placeholder="Please enter Max Rate"
                  type="input"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                      >
                        SEK/h
                      </InputAdornment>
                    )
                  }}
                  sx={{ mb: 2}}
                  wrapperSx={{width: 1}}
                  labelSx={{mb: -0.5}}
                  required
                />
                <TextField
                  name="referenceNumber"
                  label="Reference number"
                  placeholder="Please enter Reference number"
                  required
                  type="text"
                  sx={{ mb: 2}}
                  fullWidth
                />
              </Stack>

              <PlacesField name="places" label="Locations" required fullWidth wrapperSx={{mb: 2}} />
              {jobAd?.uses_nuts_locations && (
                <NutsCodes
                  name="locations"
                  label="NUTS codes"
                  required
                  fullWidth
                  wrapperSx={{mb: 2}}
                  methods={methods}
                  selectedItems={selectedLocations}
                />
              )}
              <MultiSelect multiple value={selectedCompetenceAreas} onChange={handleCompetenceAreasChange} placeholder="Please select" required fullWidth name="competenceAreas" label="Competence Areas" options={competencesArray} wrapperSx={{ mb: 2 }} />
              <AdditionalPeriods initialPeriod={initialPeriod} setInitialPeriod={setInitialPeriod} additionalPeriods={additionalPeriods} handleAdditionalPeriods={handleAdditionalPeriods} methods={methods} tender />
              <Stack direction={"row"} gap={2} sx={{mt: 2}}>
                <Select
                  name="applicationType"
                  label="Application Type"
                  options={applicationTypeOptions}
                  placeholder="Please select application type"
                  wrapperSx={{mb:1}}
                  helperText={applicationMethodLocked ? "Applications received — method can't be changed" : ''}
                  required
                  fullWidth
                />
                <DateTimeInput
                  name="lastDayToRespond"
                  label="Last day to respond"
                  required
                  fullWidth
                  minDate={dayjs()}
                  maxDate={dayjs().add(60, 'day')}
                  value={dayjs(lastDayToRespond)}
                  onChange={(date) => handleDateChange(date, "lastDayToRespond")}
                />
                <DateTimeInput
                  name="questionsEndDate"
                  label="End date for questions"
                  required
                  fullWidth
                  minDate={dayjs()}
                  maxDate={lastDayToRespond ? dayjs(lastDayToRespond) : dayjs().add(60, 'day')}
                  value={dayjs(questionsEndDate)}
                  onChange={(date) => handleDateChange(date, "questionsEndDate")}
                />
              </Stack>
              {applicationTypeValue === 'email' && (
                <TextField
                  name="applicationEmail"
                  label="Application email"
                  placeholder="Please enter Application email"
                  required
                  type="email"
                  autoComplete="email"
                  sx={{ mb: 2}}
                  fullWidth
                />
              )}

              {applicationTypeValue === 'url' && (
                <TextField
                  name="applicationURL"
                  label="Application URL"
                  placeholder="Please enter Application URL"
                  required
                  type="text"
                  sx={{ mb: 2}}
                  fullWidth
                />
              )}
            </Stack>
          </Stack>
          </LocalizationProvider>
        </FormProvider>
      </Stack>
    </Box>
  );

  return (
    <>
      <AccordionListItem onClick={() => setOpen(true)}>Edit</AccordionListItem>
      {open && ReactDOM.createPortal(EditFormContent, document.body)}
    </>
  );
};